import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";

type WorkspaceCheckState = {
  showModal: boolean;
  workspaceId: string | null;
  workspaceName: string;
  closeModal: () => void;
  markCustomised: () => Promise<void>;
};

export function useWorkspaceCheck(): WorkspaceCheckState {
  const supabase = useMemo(() => createClient(), []);
  const pathname = usePathname();
  const [showModal, setShowModal] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState("");
  const userIdRef = useRef<string | null>(null);

  const dismissedKeyForUser = useCallback((uid: string) => {
    return `ff_workspace_setup_dismissed:${uid}`;
  }, []);

  const isDismissedForUser = useCallback(
    (uid: string) => {
      try {
        return sessionStorage.getItem(dismissedKeyForUser(uid)) === "1";
      } catch {
        return false;
      }
    },
    [dismissedKeyForUser],
  );

  const markDismissedForUser = useCallback(
    (uid: string) => {
      try {
        sessionStorage.setItem(dismissedKeyForUser(uid), "1");
      } catch {
        // ignore
      }
    },
    [dismissedKeyForUser],
  );

  const clearDismissedForUser = useCallback(
    (uid: string) => {
      try {
        sessionStorage.removeItem(dismissedKeyForUser(uid));
      } catch {
        // ignore
      }
    },
    [dismissedKeyForUser],
  );

  const randomSlug = useCallback(() => {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }, []);

  const ensureWorkspaceForUser = useCallback(
    async (user: User): Promise<{ id: string; name: string } | null> => {
      const { data: ws, error: wsErr } = await supabase
        .from("workspaces")
        .insert({ name: "", slug: `ws-${randomSlug()}` })
        .select("id,name")
        .single();

      if (wsErr || !ws) return null;

      const { error: memberErr } = await supabase.from("workspace_members").insert({
        workspace_id: ws.id,
        user_id: user.id,
        role: "admin",
      });

      if (memberErr) return null;
      return ws;
    },
    [randomSlug, supabase],
  );

  const getAuthedUser = useCallback(async (): Promise<User | null> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) return user;

    const { data } = await supabase.auth.getSession();
    return data.session?.user ?? null;
  }, [supabase]);

  const checkOnce = useCallback(async (): Promise<boolean> => {
    const user = await getAuthedUser();
    if (!user) return false;
    userIdRef.current = user.id;

    const { data: members, error: memberError } = await supabase
      .from("workspace_members")
      .select("workspace_id")
      .eq("user_id", user.id)
      .limit(1);

    const workspaceIdFromMember =
      Array.isArray(members) && members.length > 0
        ? members[0]?.workspace_id ?? null
        : null;

    if (memberError) return false;

    if (!workspaceIdFromMember) {
      const created = await ensureWorkspaceForUser(user);
      if (!created) return false;
      setWorkspaceId(created.id);
      setWorkspaceName(created.name ?? "");
      setShowModal(!isDismissedForUser(user.id));
      return true;
    }

    const { data: workspace, error: workspaceError } = await supabase
      .from("workspaces")
      .select("id,name,name_customised")
      .eq("id", workspaceIdFromMember)
      .maybeSingle();

    if (workspaceError) return false;
    if (!workspace) {
      const created = await ensureWorkspaceForUser(user);
      if (!created) return false;
      setWorkspaceId(created.id);
      setWorkspaceName(created.name ?? "");
      setShowModal(!isDismissedForUser(user.id));
      return true;
    }

    setWorkspaceId(workspace.id);
    setWorkspaceName(workspace.name ?? "");
    const nameIsBlank = (workspace.name ?? "").trim().length === 0;
    const shouldShow = nameIsBlank || workspace.name_customised !== true;
    if (!shouldShow) {
      setShowModal(false);
      return true;
    }

    // If the user skipped in this session, don't nag until next login.
    if (isDismissedForUser(user.id)) {
      setShowModal(false);
      return true;
    }

    setShowModal(true);

    return true;
  }, [ensureWorkspaceForUser, getAuthedUser, isDismissedForUser, supabase]);

  const checkWithRetry = useCallback(async () => {
    const delaysMs = [0, 150, 300, 600, 1000];
    for (const delay of delaysMs) {
      if (delay > 0) {
        await new Promise((r) => setTimeout(r, delay));
      }
      const ok = await checkOnce();
      if (ok) return;
    }
  }, [checkOnce]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      await checkWithRetry();
    };

    void run();

    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === "SIGNED_IN") {
        // New login should re-enable the modal.
        const uid = userIdRef.current;
        if (uid) clearDismissedForUser(uid);
      }
      void checkWithRetry();
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [checkWithRetry, supabase, pathname]);

  const closeModal = () => {
    // If workspace isn't customized yet, treat close as "skip for this session".
    const uid = userIdRef.current;
    if (uid) markDismissedForUser(uid);
    setShowModal(false);
  };

  const markCustomised = async () => {
    if (!workspaceId) {
      setShowModal(false);
      return;
    }

    await supabase
      .from("workspaces")
      .update({ name_customised: true })
      .eq("id", workspaceId);

    setShowModal(false);
  };

  return { showModal, workspaceId, workspaceName, closeModal, markCustomised };
}

